import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Shell from './tuteur/Shell';
import { themeLabel } from './tuteur/themes';
import { slotLabel } from './FormsDrill';

// ── Tuteur personnel (page PERSONNELLE, propriétaire uniquement) ─────────────
// Accès via ?tuteur. Les données viennent de /api/tutor, réservé aux `user_id`
// inscrits dans `owners` : 401 si non connecté, 403 sinon.
//
// DISCRÉTION (même règle que ?ecoute) : qui n'y a pas droit est renvoyé à
// l'accueil sans le moindre écran d'explication. Ni « créez un compte », ni
// « espace privé » : pour tout le monde sauf le propriétaire, `?tuteur` se
// comporte comme un paramètre inconnu.
//
// UNE SEULE LANGUE, le français. C'est la page d'UNE personne, pas une section
// du site : la traduire en cinq langues coûterait cinq fois le travail de copie
// pour zéro lecteur. Toutes les autres pages restent multilingues.
//
// Parti pris : cette page ne fait pas passer d'exercice. Elle DIT quoi travailler
// et envoie vers le moteur qui existe déjà (quiz avec une liste de mots imposée,
// écoute, quiz thématique). Un deuxième moteur d'exercices serait un deuxième
// endroit où corriger les bugs.

const TOKEN_KEY = 'motrusse_token';
const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";

// Un mot est « acquis » à 21 jours d'intervalle SRS. Même seuil que le back
// (tutor.ACQUIS_DAYS) ; il n'est ici que pour l'expliquer à l'écran.
const ACQUIS_DAYS = 21;

const plural = (n, mot, pl) => `${n} ${n > 1 ? (pl || `${mot}s`) : mot}`;

// « il y a N jours » à partir d'une date ISO (jour), sans dépendance externe.
const daysSince = (iso, todayIso) => {
  if (!iso) return null;
  const a = Date.parse(`${iso}T00:00:00Z`);
  const b = Date.parse(`${todayIso}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86400000);
};

const dayLabel = (n) => (n === 0 ? "aujourd'hui" : n === 1 ? 'hier' : `il y a ${n} jours`);

// Fautes récurrentes du rappel actif. Le libellé explique le MÉCANISME, pas
// seulement le symptôme : « tu écris d'après le son » est actionnable, « faute
// d'orthographe » ne l'est pas.
const FAUTES = {
  signe_mou: ["Le ь oublié", "Il ne s'entend pas, donc il saute quand on écrit d'oreille. Il fait pourtant partie du mot, au même titre qu'une lettre."],
  o_a_atone: ["о et а non accentués", "Hors accent, les deux se prononcent pareil. À l'oreille il n'y a aucun indice : il faut savoir où tombe l'accent, ou avoir vu le mot écrit."],
  i_manquant: ["Le и de -ия, -ие", "Réduit à presque rien à l'oral, il est obligatoire à l'écrit. компания, специализация."],
  voyelle_molle: ["е, и, я confondus", "Voyelles proches à l'oreille, distinctes à l'écrit."],
  sourde_sonore: ["Sourde pour sonore", "с/з, т/д, п/б, к/г. Le russe assourdit en fin de mot et devant une sourde, donc l'oreille trompe."],
  lettre_omise: ["Une lettre sautée", "Souvent une consonne au milieu d'un groupe, là où elle s'entend mal."],
  lettre_en_trop: ["Une lettre de trop", ""],
  autre: ["Autres", ""],
};

// ── Blocs du plan : tout le texte est ici, le back n'envoie que des nombres ──
const BLOCKS = {
  retard: (b) => ({
    title: 'Rattraper le retard',
    why: b.oublies
      ? `${plural(b.n, 'mot')} attendent une révision, dont ${b.oublies} depuis plus de dix jours.`
      : `${plural(b.n, 'mot')} attendent une révision. C'est le moment où la révision rapporte le plus.`,
    cta: `Réviser ${(b.ids || []).length} mots`,
  }),
  rappel: (b) => ({
    title: 'Faire remonter les mots',
    why: b.vus === 0
      ? `${plural(b.n, 'mot')} que tu reconnais sans hésiter. Reconnaître n'est pas produire : ici tu les écris de mémoire, sans propositions.`
      : `${b.ok} produits sur ${b.vus} tentatives depuis le début. ${plural(b.n, 'mot')} sont mûrs pour cet exercice.`,
    cta: 'Écrire 15 mots de mémoire',
  }),
  // L'écart le plus large du diagnostic, donc le bloc dit un CHIFFRE et une
  // étape nommée, jamais « continue comme ça ». Un objectif qu'on ne chiffre pas
  // ne se rate jamais, il s'oublie.
  decliner: (b) => ({
    title: b.famille === 'verbes' ? 'Savoir conjuguer' : 'Savoir décliner',
    why: b.vus === 0
      ? `Tu reconnais une forme 8 fois sur 10 et tu n'en produis une que 1 fois sur 10. On reprend à zéro, une forme à la fois : ${b.titre.toLowerCase()}, étape ${b.rang} sur ${b.etapes}, ${b.etape.toLowerCase()}, ${b.quand}.`
      : `${b.titre} · étape ${b.rang} sur ${b.etapes} : ${b.etape.toLowerCase()}. ${b.tenues} classes sur ${b.classes} tiennent. Il en reste ${b.classes - b.tenues} avant l'étape suivante.`,
    cta: 'Écrire 30 terminaisons',
  }),
  formes: (b) => ({
    title: 'Les formes de tes mots',
    why: b.weak
      ? `Ton point faible du moment : ${slotLabel(b.weak)}, ${Math.round(b.weak_rate * 100)} % de réussite. Les mots que tu travailles apparaissent sous ${b.n} formes fléchies dans les phrases du site.`
      : `Les mots que tu travailles apparaissent sous ${b.n} formes fléchies dans les phrases du site. Reconnaître le mot déguisé, c'est ce qui manque le plus.`,
    cta: 'Faire une série de 10',
  }),
  ecrire: (b) => ({
    title: b.niveau === 1 ? 'Écrire une phrase'
      : b.niveau === 2 ? 'Écrire un paragraphe' : "Écrire une lettre d'examen",
    why: b.copies === 0
      ? "Tu n'as encore rien écrit. On commence petit : une seule phrase, avec la structure donnée."
      : `${plural(b.copies, 'copie')} rendue${b.copies > 1 ? 's' : ''}. Il reste ${plural(b.reste, 'sujet')} à cette marche.`,
    cta: b.niveau === 1 ? 'Écrire une phrase' : 'Ouvrir l\'atelier',
  }),
  resistants: (b) => ({
    title: 'Les mots qui résistent',
    why: `${plural(b.n, 'mot')} te font échouer plus souvent que les autres. Ils reviennent vite et repartent aussitôt.`,
    cta: `Reprendre ces ${(b.ids || []).length} mots`,
  }),
  neufs: (b) => ({
    title: 'Ouvrir du terrain neuf',
    why: `Il te manque ${plural(b.n, 'mot')} dans les ${b.band} plus fréquents (${b.cefr}). Ce sont les plus rentables à apprendre.`,
    cta: `Découvrir ${(b.ids || []).length} mots`,
  }),
  ecoute: (b) => ({
    title: 'Écouter du russe',
    why: `${plural(b.n, 'texte')} que tu n'as jamais travaillés. Le prochain est en ${b.level}.`,
    cta: "Aller à l'écoute",
  }),
  theme: (b) => ({
    title: 'Un thème à combler',
    // On annonce la COUVERTURE et non la part acquise : « 0 acquis sur 130 » ne
    // dit rien d'utile tant que presque rien n'a atteint les trois semaines.
    why: `Sur « ${themeLabel(b.slug)} », tu n'as rencontré que ${plural(b.seen, 'mot')} sur ${b.total}. C'est ton champ le moins couvert.`,
    cta: `Travailler ${(b.ids || []).length} mots du thème`,
  }),
};

const blockHref = (b) => {
  if (b.kind === 'ecoute') return '/?ecoute';
  if (b.kind === 'formes') return '/?formes';
  if (b.kind === 'decliner') return '/?decliner';
  if (b.kind === 'ecrire') return '/?ecrire';
  if (b.kind === 'rappel') return '/?rappel';
  // Toutes les séries de mots passent par le MOTEUR DU TUTEUR (/?serie=), jamais
  // plus par le quiz public : c'est de là que venaient les régressions.
  return `/?serie=${(b.ids || []).join(',')}`;
};

const Section = ({ title, hint, children }) => (
  <section className="flex flex-col gap-3">
    <div>
      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">{title}</h2>
      {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    </div>
    {children}
  </section>
);

// Une brique de la dose du jour : fait / cible, avec une barre. Afficher la
// CIBLE change tout — un objectif qu'on ne montre pas ne se rate jamais, il
// s'oublie, et c'est comme ça qu'on entretient un niveau en croyant progresser.
const DoseBar = ({ label, fait, cible }) => {
  const pct = Math.min(100, Math.round((fait / cible) * 100));
  const ok = fait >= cible;
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 shrink-0 text-xs text-slate-400">{label}</div>
      <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
        <div className={`h-full ${ok ? 'bg-blue-500' : 'bg-slate-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <div className={`w-16 shrink-0 text-right text-xs tabular-nums ${ok ? 'text-blue-300' : 'text-slate-500'}`}>
        {fait} / {cible}
      </div>
    </div>
  );
};

const Tile = ({ value, label, accent }) => (
  <div className="bg-slate-800 border border-slate-700 rounded-xl px-3 py-3 text-center">
    <div className={`text-2xl font-bold ${accent || 'text-white'}`}>{value}</div>
    <div className="text-[11px] text-slate-400 leading-tight mt-0.5">{label}</div>
  </div>
);

// Barre de couverture d'une bande de fréquence : la part ACQUISE (bleu plein) se
// lit dans la part VUE (bleu pâle). Deux mesures très différentes qu'un seul
// pourcentage confondrait — voir un mot une fois n'est pas le connaître.
const BandBar = ({ band }) => {
  const seen = band.total ? Math.round((band.seen / band.total) * 100) : 0;
  const acquis = band.total ? Math.round((band.acquis / band.total) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-24 shrink-0 text-xs text-slate-400">
        {band.max_rank} <span className="text-slate-600">{band.cefr}</span>
      </div>
      <div className="flex-1 h-2.5 rounded-full bg-slate-800 overflow-hidden relative">
        <div className="absolute inset-y-0 left-0 bg-blue-500/25" style={{ width: `${seen}%` }} />
        <div className="absolute inset-y-0 left-0 bg-blue-500" style={{ width: `${acquis}%` }} />
      </div>
      <div className="w-24 shrink-0 text-right text-xs tabular-nums text-slate-400">
        {band.seen}<span className="text-slate-600">/{band.total}</span>
      </div>
    </div>
  );
};

export default function Tutor({ cursive, toggleCursive }) {
  const [phase, setPhase] = useState('loading'); // loading | error | ready (sinon : redirection)
  const [data, setData] = useState(null);
  const [started, setStarted] = useState([]);
  const [token] = useState(() => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  });

  useEffect(() => {
    const leave = () => { try { window.location.replace('/'); } catch {} };
    if (!token) { leave(); return; }
    fetch('/api/tutor', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { leave(); return null; }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => {
        if (d == null) return;
        setData(d);
        setStarted(d.plan?.started || []);
        setPhase('ready');
      })
      .catch(() => setPhase('error'));
  }, [token]);

  // Lancer un exercice prescrit : on JOURNALISE d'abord, on navigue ensuite.
  // Sans attendre la réponse du serveur on perdrait la trace à chaque fois que la
  // navigation coupe la requête, et le suivi de régularité mentirait par défaut.
  // `keepalive` couvre le cas où le navigateur part quand même.
  const go = useCallback((b) => (e) => {
    e.preventDefault();
    const href = blockHref(b);
    const done = () => { window.location.href = href; };
    if (!token) { done(); return; }
    fetch('/api/tutor/start', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      // On envoie les identifiants prescrits : le suivi doit se faire contre
      // CE lot-là, pas contre la liste que le plan recalculera demain.
      // `cible` : la prescription non numérique (le slug d'un thème). Sans elle,
      // le thème le moins couvert se recalcule et change pendant qu'on le fait.
      body: JSON.stringify({
        kind: b.kind, n: (b.ids || []).length, ids: b.ids || [], cible: b.slug || null,
      }),
      keepalive: true,
    }).then(done, done);
  }, [token]);

  const opening = useMemo(() => {
    if (!data) return null;
    const c = data.vocab.counts;
    const gap = daysSince(data.profile.last_activity, data.today);
    if (!c.vus) return "On part de zéro. Tant mieux, c'est le moment où chaque mot rapporte le plus.";
    if (gap === null) return `${plural(c.vus, 'mot')} travaillés, ${c.acquis} acquis.`;
    if (gap >= 7) return `Dernière séance ${dayLabel(gap)}. ${plural(c.dus, 'mot')} t'attendent : reprends par le rattrapage.`;
    if (gap === 0) return `Déjà passé aujourd'hui. ${plural(c.dus, 'mot')} restent à revoir.`;
    return `Dernière séance ${dayLabel(gap)}. ${plural(c.dus, 'mot')} à revoir aujourd'hui.`;
  }, [data]);

  // Bandeau de régularité : les 14 derniers jours. Une case pleine = un jour où
  // au moins un mot a été révisé. On lit `last_reviewed`, qui ne garde que la
  // DERNIÈRE révision de chaque mot : un jour peut donc paraître plus léger qu'il
  // ne l'a été, jamais plus chargé. La présence, elle, est certaine.
  const strip = useMemo(() => {
    if (!data) return [];
    const out = [];
    const t = Date.parse(`${data.today}T00:00:00Z`);
    for (let i = 13; i >= 0; i -= 1) {
      const iso = new Date(t - i * 86400000).toISOString().slice(0, 10);
      out.push({ iso, n: data.vocab.activity[iso] || 0, plan: (data.plan.followed_days || []).includes(iso) });
    }
    return out;
  }, [data]);

  if (phase === 'loading') {
    return (
      <Shell actif="/?tuteur" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="flex items-center justify-center py-24 text-slate-400">
          <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        </div>
      </Shell>
    );
  }

  if (phase === 'error') {
    return (
      <Shell actif="/?tuteur" cursive={cursive} toggleCursive={toggleCursive}>
        <div className="text-center flex flex-col items-center gap-4 pt-16">
          <p className="text-slate-400">Impossible de charger ton bilan.</p>
          <button onClick={() => window.location.reload()} className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg font-medium transition-colors">
            Réessayer
          </button>
        </div>
      </Shell>
    );
  }

  const { profile, vocab, listening, formes, plan, dose, fautes } = data;
  const c = vocab.counts;
  const lt = profile.level_test;
  const tiers = listening.tiers || {};
  const tierRows = [['facile', 'Facile'], ['moyen', 'Moyen'], ['difficile', 'Difficile']]
    .map(([k, label]) => ({ k, label, ...(tiers[k] || { total: 0, correct: 0 }) }))
    .filter((t) => t.total > 0);

  return (
    <Shell actif="/?tuteur" cursive={cursive} toggleCursive={toggleCursive}>
      <div className="flex flex-col gap-1">
        <h1 className="text-xl md:text-2xl font-bold text-white">Ton tuteur</h1>
        <p className="text-sm text-slate-400">{opening}</p>
      </div>

      {/* ── La dose du jour ─────────────────────────────────────────────── */}
      {dose && (
        <Section
          title="La dose du jour"
          hint={`${dose.jours_restants} jours avant l'examen, soit ${dose.semaines_restantes} semaines. Ces cibles visent le B2, pas l'entretien.`}
        >
          <div className="flex flex-col gap-2">
            <DoseBar label="mots révisés" fait={dose.fait.mots} cible={dose.cible.mots} />
            <DoseBar label="rappel actif" fait={dose.fait.rappel} cible={dose.cible.rappel} />
            <DoseBar label="décliner" fait={dose.fait.decliner} cible={dose.cible.decliner} />
            <DoseBar label="formes" fait={dose.fait.formes} cible={dose.cible.formes} />
            <DoseBar label="écoute" fait={dose.fait.ecoute} cible={dose.cible.ecoute} />
            <DoseBar label="écrire" fait={dose.fait.ecrire} cible={dose.cible.ecrire} />
            {/* La DURÉE du contrat, annoncée. Le plan a été recalibré sur l'heure
                par jour que tu as annoncée : sans le chiffre affiché, personne ne
                peut savoir si la journée tient dans sa soirée. */}
            {dose.minutes > 0 && (
              <p className="text-[11px] text-slate-600 pt-1">
                {dose.minutes_restantes > 0
                  ? `≈ ${dose.minutes_restantes} min pour finir la journée, sur ${dose.minutes} min au total.`
                  : `Journée bouclée. Le contrat est d'environ ${dose.minutes} min.`}
              </p>
            )}
          </div>
          <p className="text-xs text-slate-500">
            {dose.briques_faites === dose.briques
              ? 'Toutes les cibles sont atteintes. Journée pleine.'
              : `${dose.briques_faites} sur ${dose.briques} atteintes. Compte environ une heure pour l'ensemble.`}
          </p>
        </Section>
      )}

      {/* ── Le plan du jour ─────────────────────────────────────────────── */}
      <Section title="Le plan du jour" hint="Dans l'ordre. Le premier bloc suffit les jours pressés.">
        {plan.blocks.length === 0 && (
          <p className="text-sm text-slate-400">
            Rien à rattraper aujourd'hui. Va faire une série normale, ou reviens demain.
          </p>
        )}
        <div className="flex flex-col gap-3">
          {plan.blocks.map((b, i) => {
            // Un `kind` inconnu (bloc ajouté côté serveur sans libellé côté
            // front) rendait TOUTE la page blanche. On saute la carte plutôt.
            if (!BLOCKS[b.kind]) return null;
            const t = BLOCKS[b.kind](b);
            const done = started.includes(b.kind);
            return (
              <div key={b.kind} className="bg-slate-800 border border-slate-700 rounded-2xl p-4 flex flex-col gap-3">
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 rounded-full bg-slate-700 text-slate-300 text-xs font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white">{t.title}</h3>
                    <p className="text-sm text-slate-400 mt-0.5">{t.why}</p>
                  </div>
                </div>
                {/* Objectif CHIFFRÉ et état réel. « Lancé aujourd'hui » ne
                    disait rien : on peut ouvrir un exercice et le refermer. */}
                {b.objectif != null && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-1.5 rounded-full bg-slate-700 overflow-hidden">
                      <div
                        className={`h-full ${b.termine ? 'bg-blue-500' : 'bg-slate-500'}`}
                        style={{ width: `${Math.min(100, Math.round((b.fait / b.objectif) * 100))}%` }}
                      />
                    </div>
                    <span className={`text-xs tabular-nums shrink-0 ${b.termine ? 'text-blue-300' : 'text-slate-500'}`}>
                      {b.fait} / {b.objectif} {b.unite}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <a
                    href={blockHref(b)}
                    onClick={go(b)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      b.termine ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-blue-600 hover:bg-blue-500'
                    }`}
                  >
                    {b.termine ? 'Refaire' : t.cta}
                  </a>
                  {b.termine ? (
                    <span className="text-xs text-blue-300">fait</span>
                  ) : done ? (
                    <span className="text-xs text-slate-500">commencé, pas fini</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* ── Où tu en es ─────────────────────────────────────────────────── */}
      <Section title="Où tu en es">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Tile value={c.vus} label="mots rencontrés" />
          <Tile value={c.acquis} label={`acquis (${ACQUIS_DAYS} j et +)`} accent="text-blue-300" />
          <Tile value={c.dus} label="à revoir" />
          <Tile value={c.resistants} label="qui résistent" />
        </div>
        <p className="text-xs text-slate-500">
          « Acquis » veut dire que le mot a survécu à un intervalle de trois semaines. En dessous,
          rien ne prouve encore qu'il tienne.
          {lt && ` Ton dernier test de niveau donnait ${lt.words} mots (${lt.cefr}).`}
        </p>
        <div className="flex flex-col gap-2 mt-1">
          {vocab.bands.map((b) => <BandBar key={b.max_rank} band={b} />)}
        </div>
        <p className="text-xs text-slate-500">
          Barre pâle : mots rencontrés. Barre pleine : mots acquis. Les trous du haut du tableau
          comptent plus que ceux du bas.
        </p>
      </Section>

      {/* ── Les mots qui résistent ──────────────────────────────────────── */}
      {vocab.resistants.length > 0 && (
        <Section
          title="Les mots qui résistent"
          hint="Classés du plus rétif au moins rétif. La mesure vient de tes réponses, pas d'une impression."
        >
          <div className="flex flex-col divide-y divide-slate-800 border border-slate-700 rounded-xl overflow-hidden bg-slate-800/40">
            {vocab.resistants.map((w) => (
              <a
                key={w.id}
                href={w.page}
                className="flex items-baseline gap-3 px-4 py-2.5 hover:bg-slate-800 transition-colors"
              >
                <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300 text-lg shrink-0">
                  {w.russe}
                </span>
                <span className="text-sm text-slate-300 truncate">{w.francais}</span>
                <span className="ml-auto shrink-0 text-[11px] text-slate-500 tabular-nums">
                  {w.reussites > 0 ? `${w.reussites} d'affilée` : 'aucune série'}
                  {' · '}rang {w.rank}
                </span>
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* ── Fautes qui reviennent ───────────────────────────────────────── */}
      {fautes && fautes.length > 0 && (
        <Section
          title="Tes fautes qui reviennent"
          hint="Classées automatiquement à partir de ce que tu tapes au rappel actif."
        >
          <div className="flex flex-col gap-3">
            {fautes.slice(0, 5).map((f) => {
              const [titre, explic] = FAUTES[f.code] || [f.code, ''];
              return (
                <div key={f.code} className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="font-semibold text-white">{titre}</h3>
                    <span className="text-xs text-slate-500">{f.n} fois</span>
                  </div>
                  {explic && <p className="text-sm text-slate-400 mt-1">{explic}</p>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {f.exemples.map((e, k) => (
                      <span key={k} className="text-xs text-slate-500">
                        <span lang="ru" style={{ fontFamily: SERIF }}>{e.essai}</span>
                        {' → '}
                        <span lang="ru" style={{ fontFamily: SERIF }} className="text-blue-300">{e.russe}</span>
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* ── Formes ──────────────────────────────────────────────────────── */}
      {formes && (formes.total > 0 || formes.dispo > 0) && (
        <Section
          title="Terminaisons"
          hint={formes.total > 0
            ? 'Réussite par case, sur tout ton historique. Les cases vues moins de quatre fois ne sont pas montrées.'
            : undefined}
        >
          {formes.total === 0 ? (
            <p className="text-sm text-slate-400">
              {formes.dispo} formes fléchies de tes mots attendent dans les phrases du site.
              Une série et cette section te dira quelles terminaisons tiennent.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-400">
                {formes.ok} sur {formes.total} depuis le début.
              </p>
              <div className="flex flex-col gap-1.5">
                {Object.entries(formes.slots)
                  .filter(([s, v]) => s && v.n >= 4)
                  .sort((a, b) => a[1].ok / a[1].n - b[1].ok / b[1].n)
                  .map(([s, v]) => {
                    const pct = Math.round((v.ok / v.n) * 100);
                    return (
                      <div key={s} className="flex items-center gap-3">
                        <div className="w-40 shrink-0 text-xs text-slate-400 truncate">{slotLabel(s)}</div>
                        <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                          <div className={`h-full ${pct < 60 ? 'bg-slate-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <div className="w-16 shrink-0 text-right text-xs tabular-nums text-slate-500">
                          {pct} %
                        </div>
                      </div>
                    );
                  })}
              </div>
            </>
          )}
          <div className="flex gap-4 flex-wrap">
            <a href="/?ecrire" className="text-xs text-blue-300 hover:text-blue-200 transition-colors">
              Atelier d'écriture
            </a>
            <a href="/?decliner" className="text-xs text-blue-300 hover:text-blue-200 transition-colors">
              Échelle de déclinaison
            </a>
            <a href="/?formes" className="text-xs text-blue-300 hover:text-blue-200 transition-colors">
              Ouvrir l'exercice de formes
            </a>
            <a href="/?tableaux" className="text-xs text-blue-300 hover:text-blue-200 transition-colors">
              Voir les tableaux
            </a>
          </div>
        </Section>
      )}

      {/* ── Écoute ──────────────────────────────────────────────────────── */}
      <Section title="Compréhension orale">
        <p className="text-sm text-slate-400">
          {listening.done} textes travaillés sur {listening.total}.
        </p>
        {tierRows.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {tierRows.map((t) => (
              <Tile
                key={t.k}
                value={`${Math.round((t.correct / t.total) * 100)} %`}
                label={`${t.label} (${t.total})`}
                accent={t.correct / t.total < 0.6 ? 'text-white' : 'text-blue-300'}
              />
            ))}
          </div>
        )}
        {tierRows.length > 0 && (
          <p className="text-xs text-slate-500">
            Les questions faciles portent sur ce qui est dit, les difficiles sur ce qui est sous-entendu.
            Un écart entre les deux dit où travailler.
          </p>
        )}
      </Section>

      {/* ── Régularité ──────────────────────────────────────────────────── */}
      <Section title="Régularité" hint="Quatorze derniers jours. Un point clair = une séance.">
        <div className="flex items-end gap-1.5">
          {strip.map((d) => (
            <div key={d.iso} className="flex-1 flex flex-col items-center gap-1" title={`${d.iso} : ${d.n} mots`}>
              <div className={`w-full h-8 rounded ${d.n ? 'bg-blue-500' : 'bg-slate-800'}`} />
              <div className={`w-1.5 h-1.5 rounded-full ${d.plan ? 'bg-blue-300' : 'bg-transparent'}`} />
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500">
          Le petit point sous la barre marque les jours où tu as lancé un exercice du plan.
          Total depuis le début : {plural(profile.days, 'jour')} d'entraînement, {plural(profile.series, 'série')}.
        </p>
      </Section>
    </Shell>
  );
}
